import React from "react";
import { dictionaries } from "./translations";

export const LanguageContext = React.createContext({ language: "bs", setLanguage: () => {}, t: value => value });
const valueToCode = { bosnian: "bs", english: "en", greman: "de", german: "de", franch: "fr", french: "fr", slovenian: "sl", bs: "bs", en: "en", de: "de", fr: "fr", sl: "sl" };
const codeToValue = { bs: "bosnian", en: "english", de: "greman", fr: "franch", sl: "slovenian" };

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = React.useState(() => localStorage.getItem("alphaFocusLanguage") || "bs");
  const originals = React.useRef(new WeakMap());
  const originalAttributes = React.useRef(new WeakMap());

  const t = React.useCallback(value => dictionaries[language]?.[String(value).trim()] || value, [language]);

  React.useEffect(() => {
    localStorage.setItem("alphaFocusLanguage", language);
    document.documentElement.lang = language;
    const translateNode = node => {
      if (window.location.pathname.startsWith("/admin")) return;
      if (node.nodeType === Node.TEXT_NODE) {
        if (!node.nodeValue.trim() || ["SCRIPT", "STYLE"].includes(node.parentElement?.tagName)) return;
        if (!originals.current.has(node)) originals.current.set(node, node.nodeValue);
        const original = originals.current.get(node);
        const clean = original.trim();
        const numbered = clean.match(/^(\d+\.\s+)(.*)$/);
        const translated = dictionaries[language]?.[clean] || (numbered && dictionaries[language]?.[numbered[2]] ? `${numbered[1]}${dictionaries[language][numbered[2]]}` : null);
        if (translated) node.nodeValue = original.replace(clean, translated);
        else if (language === "bs") node.nodeValue = original;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        ["placeholder", "title", "aria-label"].forEach(attribute => {
          if (!node.hasAttribute?.(attribute)) return;
          let values = originalAttributes.current.get(node) || {};
          if (!values[attribute]) values[attribute] = node.getAttribute(attribute);
          originalAttributes.current.set(node, values);
          node.setAttribute(attribute, dictionaries[language]?.[values[attribute]] || values[attribute]);
        });
        node.childNodes.forEach(translateNode);
      }
    };
    translateNode(document.body);
    document.querySelectorAll('select[name="language"]').forEach(select => { select.value = codeToValue[language]; });
    const observer = new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(translateNode)));
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [language]);

  React.useEffect(() => {
    const changeLanguage = event => {
      if (event.target.matches('select[name="language"]')) setLanguage(valueToCode[event.target.value] || "bs");
    };
    document.addEventListener("change", changeLanguage);
    return () => document.removeEventListener("change", changeLanguage);
  }, []);

  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => React.useContext(LanguageContext);
export default LanguageProvider;
